import { createContext, useContext, useEffect, useState } from 'react'

interface GeoPrice {
  offerPrice: string
  originalPrice: string
  currency: string
  symbol: string
  label: string
  loading: boolean
}

const PRICE_MAP: Record<
  string,
  {
    offer: string
    original: string
    symbol: string
    currency: string
    label: string
  }
> = {
  MX: {
    offer: '139',
    original: '1,149',
    symbol: '$',
    currency: 'MXN',
    label: 'MXN',
  },
  CO: {
    offer: '33,500',
    original: '279,000',
    symbol: '$',
    currency: 'COP',
    label: 'COP',
  },
  AR: {
    offer: '7,990',
    original: '67,000',
    symbol: '$',
    currency: 'ARS',
    label: 'ARS',
  },
  CL: {
    offer: '7,499',
    original: '62,900',
    symbol: '$',
    currency: 'CLP',
    label: 'CLP',
  },
  PE: {
    offer: '29.99',
    original: '249',
    symbol: 'S/',
    currency: 'PEN',
    label: 'PEN',
  },
  BR: {
    offer: '39.99',
    original: '335',
    symbol: 'R$',
    currency: 'BRL',
    label: 'BRL',
  },
  ES: {
    offer: '7.49',
    original: '62',
    symbol: '€',
    currency: 'EUR',
    label: 'EUR',
  },
  FR: {
    offer: '7.49',
    original: '62',
    symbol: '€',
    currency: 'EUR',
    label: 'EUR',
  },
  DE: {
    offer: '7.49',
    original: '62',
    symbol: '€',
    currency: 'EUR',
    label: 'EUR',
  },
  IT: {
    offer: '7.49',
    original: '62',
    symbol: '€',
    currency: 'EUR',
    label: 'EUR',
  },
  PT: {
    offer: '7.49',
    original: '62',
    symbol: '€',
    currency: 'EUR',
    label: 'EUR',
  },
  GB: {
    offer: '6.49',
    original: '53',
    symbol: '£',
    currency: 'GBP',
    label: 'GBP',
  },
  EC: {
    offer: '7.99',
    original: '67',
    symbol: '$',
    currency: 'USD',
    label: 'USD',
  },
  PA: {
    offer: '7.99',
    original: '67',
    symbol: '$',
    currency: 'USD',
    label: 'USD',
  },
  SV: {
    offer: '7.99',
    original: '67',
    symbol: '$',
    currency: 'USD',
    label: 'USD',
  },
  GT: {
    offer: '61.90',
    original: '519',
    symbol: 'Q',
    currency: 'GTQ',
    label: 'GTQ',
  },
  CR: {
    offer: '4,099',
    original: '34,400',
    symbol: '₡',
    currency: 'CRC',
    label: 'CRC',
  },
  DO: {
    offer: '469',
    original: '3,929',
    symbol: 'RD$',
    currency: 'DOP',
    label: 'DOP',
  },
  UY: {
    offer: '319',
    original: '2,679',
    symbol: '$',
    currency: 'UYU',
    label: 'UYU',
  },
  BO: {
    offer: '54.99',
    original: '462',
    symbol: 'Bs',
    currency: 'BOB',
    label: 'BOB',
  },
  PY: {
    offer: '59,900',
    original: '502,000',
    symbol: '₲',
    currency: 'PYG',
    label: 'PYG',
  },
  HN: {
    offer: '197',
    original: '1,649',
    symbol: 'L',
    currency: 'HNL',
    label: 'HNL',
  },
  NI: {
    offer: '293',
    original: '2,459',
    symbol: 'C$',
    currency: 'NIO',
    label: 'NIO',
  },
  VE: {
    offer: '293',
    original: '2,459',
    symbol: 'Bs',
    currency: 'VES',
    label: 'VES',
  },
}

const DEFAULT_PRICE: GeoPrice = {
  offerPrice: '7.99',
  originalPrice: '67',
  symbol: '$',
  currency: 'USD',
  label: 'USD',
  loading: true,
}

export const GeoPriceContext = createContext<GeoPrice>(DEFAULT_PRICE)

export function useGeoPrice(): GeoPrice {
  return useContext(GeoPriceContext)
}

export function useGeoPriceLoader(): GeoPrice {
  const [geoPrice, setGeoPrice] = useState<GeoPrice>(DEFAULT_PRICE)

  useEffect(() => {
    const cached = sessionStorage.getItem('las3jarras_geo_v2')
    if (cached) {
      try {
        setGeoPrice({ ...JSON.parse(cached), loading: false })
        return
      } catch {}
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 3000)

    fetch('https://ipapi.co/json/', { signal: controller.signal })
      .then((res) => res.json())
      .then((data) => {
        clearTimeout(timeout)
        const country = data?.country_code || 'US'
        const mapped = PRICE_MAP[country]

        const result: GeoPrice = mapped
          ? {
              offerPrice: mapped.offer,
              originalPrice: mapped.original,
              symbol: mapped.symbol,
              currency: mapped.currency,
              label: mapped.label,
              loading: false,
            }
          : { ...DEFAULT_PRICE, loading: false }

        sessionStorage.setItem('las3jarras_geo_v2', JSON.stringify(result))
        setGeoPrice(result)
      })
      .catch(() => {
        clearTimeout(timeout)
        setGeoPrice({ ...DEFAULT_PRICE, loading: false })
      })

    return () => {
      clearTimeout(timeout)
      controller.abort()
    }
  }, [])

  return geoPrice
}
